import searchIcon from "../assets/images/search6.png";
import React, { useState, useEffect } from "react";

// Content Loader Component - Reused from the previous code
const ContentLoader = ({ isMobile }) => {
  const shimmerAnimation = `
    @keyframes shimmer {
      0% {
        background-position: -468px 0;
      }
      100% {
        background-position: 468px 0;
      }
    }
  `;

  const loaderStyles = {
    container: {
      padding: "20px",
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      backgroundColor: "#fff",
      position: "relative",
    },
    line: {
      height: isMobile ? "12px" : "14px",
      borderRadius: "4px",
      marginBottom: "8px",
      backgroundColor: "#e0e0e0",
      backgroundImage: "linear-gradient(to right, #f0f0f0 0%, #e8e8e8 20%, #f0f0f0 40%, #f0f0f0 100%)",
      backgroundRepeat: "no-repeat",
      backgroundSize: "800px 104px",
      animation: "shimmer 1.2s linear infinite",
    },
    heading: {
      width: "70%",
      height: isMobile ? "20px" : "24px",
      borderRadius: "4px",
      marginBottom: "20px",
      backgroundColor: "#e0e0e0",
      backgroundImage: "linear-gradient(to right, #f0f0f0 0%, #e8e8e8 20%, #f0f0f0 40%, #f0f0f0 100%)",
      backgroundRepeat: "no-repeat",
      backgroundSize: "800px 104px",
      animation: "shimmer 1.2s linear infinite",
    },
  };

  return (
    <>
      <style>{shimmerAnimation}</style>
      <div style={loaderStyles.container}>
        <div style={loaderStyles.heading}></div>
        <div style={{ ...loaderStyles.line, width: "100%" }}></div>
        <div style={{ ...loaderStyles.line, width: "80%" }}></div>
        <div style={{ ...loaderStyles.line, width: "90%" }}></div>
        <div style={{ ...loaderStyles.line, width: "75%" }}></div>
        <div style={{ ...loaderStyles.line, width: "85%" }}></div>
      </div>
    </>
  );
};

const HelpBerymo = () => {
  const [query, setQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isFocused, setIsFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const topics = [
    "Sign up as a restaurant",
    "Menu management",
    "Order processing",
    "Payments & earnings",
    "Delivery logistics",
    "Promotions & marketing",
    "Account settings",
    "Customer feedback",
    "Operating hours",
    "Safety & hygiene",
    "Analytics & reports",
    "Technical issues",
  ];

  const topicContent = {
    "Sign up as a restaurant": {
      heading: "Joining Berymo as a Restaurant",
      content: (
        <>
          <p style={{ marginBottom: "16px", fontSize: isMobile ? "16px" : "17px" }}>
            Partnering with Berymo allows your restaurant to reach more customers and grow your business.
          </p>
          <p style={{ fontWeight: "bold", marginBottom: "8px", fontSize: isMobile ? "19px" : "19px" }}>
            Steps to sign up:
          </p>
          <ul style={{ listStyleType: "disc", paddingLeft: "20px", marginBottom: "16px", fontSize: isMobile ? "15px" : "15px" }}>
            <li>Visit the Berymo Places signup page in the app.</li>
            <li>Fill in your restaurant details, including name, address, and contact information.</li>
            <li>Submit your application and wait for approval.</li>
            <li>Once approved, set up your menu and start receiving orders.</li>
          </ul>
        </>
      ),
    },
    "Menu management": {
      heading: "Managing Your Menu",
      content: (
        <>
          <p style={{ marginBottom: "16px", fontSize: isMobile ? "16px" : "17px" }}>
            Keep your menu up-to-date to ensure customers see the correct items and prices.
          </p>
          <p style={{ fontWeight: "bold", marginBottom: "8px", fontSize: isMobile ? "19px" : "19px" }}>
            To update your menu:
          </p>
          <ul style={{ listStyleType: "disc", paddingLeft: "20px", marginBottom: "16px", fontSize: isMobile ? "15px" : "15px" }}>
            <li>Go to the Menu Management section in the app.</li>
            <li>Add, edit, or remove items as needed.</li>
            <li>Update prices and availability.</li>
            <li>Save changes to make them live.</li>
          </ul>
        </>
      ),
    },
    "Order processing": {
      heading: "Processing Orders",
      content: (
        <>
          <p style={{ marginBottom: "16px", fontSize: isMobile ? "16px" : "17px" }}>
            Efficient order processing ensures customer satisfaction and smooth operations.
          </p>
          <p style={{ fontWeight: "bold", marginBottom: "8px", fontSize: isMobile ? "19px" : "19px" }}>
            To process orders:
          </p>
          <ul style={{ listStyleType: "disc", paddingLeft: "20px", marginBottom: "16px", fontSize: isMobile ? "15px" : "15px" }}>
            <li>Monitor incoming orders in the app.</li>
            <li>Accept or reject orders based on availability.</li>
            <li>Prepare the order and mark it as ready for pickup.</li>
            <li>Track the delivery status if applicable.</li>
          </ul>
        </>
      ),
    },
    "Payments & earnings": {
      heading: "Managing Payments and Earnings",
      content: (
        <>
          <p style={{ marginBottom: "16px", fontSize: isMobile ? "16px" : "17px" }}>
            Berymo handles payments and transfers your earnings directly to your account.
          </p>
          <p style={{ fontWeight: "bold", marginBottom: "8px", fontSize: isMobile ? "19px" : "19px" }}>
            To view earnings:
          </p>
          <ul style={{ listStyleType: "disc", paddingLeft: "20px", marginBottom: "16px", fontSize: isMobile ? "15px" : "15px" }}>
            <li>Go to the Payments & Earnings section.</li>
            <li>View your daily, weekly, or monthly earnings.</li>
            <li>Check payment schedules and history.</li>
          </ul>
        </>
      ),
    },
    "Delivery logistics": {
      heading: "Managing Delivery Logistics",
      content: (
        <>
          <p style={{ marginBottom: "16px", fontSize: isMobile ? "16px" : "17px" }}>
            Berymo coordinates delivery logistics to ensure timely delivery of orders.
          </p>
          <p style={{ fontWeight: "bold", marginBottom: "8px", fontSize: isMobile ? "19px" : "19px" }}>
            To manage deliveries:
          </p>
          <ul style={{ listStyleType: "disc", paddingLeft: "20px", marginBottom: "16px", fontSize: isMobile ? "15px" : "15px" }}>
            <li>Track delivery drivers in real-time.</li>
            <li>Communicate with drivers if there are delays.</li>
            <li>Ensure orders are ready for pickup on time.</li>
          </ul>
        </>
      ),
    },
    "Promotions & marketing": {
      heading: "Promotions and Marketing",
      content: (
        <>
          <p style={{ marginBottom: "16px", fontSize: isMobile ? "16px" : "17px" }}>
            Boost your restaurant’s visibility with Berymo’s promotional tools.
          </p>
          <p style={{ fontWeight: "bold", marginBottom: "8px", fontSize: isMobile ? "19px" : "19px" }}>
            To create a promotion:
          </p>
          <ul style={{ listStyleType: "disc", paddingLeft: "20px", marginBottom: "16px", fontSize: isMobile ? "15px" : "15px" }}>
            <li>Go to the Promotions & Marketing section.</li>
            <li>Create a discount or special offer.</li>
            <li>Set the duration and target audience.</li>
            <li>Launch the promotion and track its performance.</li>
          </ul>
        </>
      ),
    },
    "Account settings": {
      heading: "Account Settings",
      content: (
        <>
          <p style={{ marginBottom: "16px", fontSize: isMobile ? "16px" : "17px" }}>
            Customize your restaurant’s account settings to fit your needs.
          </p>
          <p style={{ fontWeight: "bold", marginBottom: "8px", fontSize: isMobile ? "19px" : "19px" }}>
            To update settings:
          </p>
          <ul style={{ listStyleType: "disc", paddingLeft: "20px", marginBottom: "16px", fontSize: isMobile ? "15px" : "15px" }}>
            <li>Go to Account Settings in the app.</li>
            <li>Update contact details, payment methods, or notifications.</li>
            <li>Save your changes.</li>
          </ul>
        </>
      ),
    },
    "Customer feedback": {
      heading: "Handling Customer Feedback",
      content: (
        <>
          <p style={{ marginBottom: "16px", fontSize: isMobile ? "16px" : "17px" }}>
            Customer feedback helps you improve your restaurant’s service.
          </p>
          <p style={{ fontWeight: "bold", marginBottom: "8px", fontSize: isMobile ? "19px" : "19px" }}>
            To view feedback:
          </p>
          <ul style={{ listStyleType: "disc", paddingLeft: "20px", marginBottom: "16px", fontSize: isMobile ? "15px" : "15px" }}>
            <li>Go to the Customer Feedback section.</li>
            <li>Read reviews and ratings from customers.</li>
            <li>Respond to feedback to address concerns.</li>
          </ul>
        </>
      ),
    },
    "Operating hours": {
      heading: "Setting Operating Hours",
      content: (
        <>
          <p style={{ marginBottom: "16px", fontSize: isMobile ? "16px" : "17px" }}>
            Ensure your operating hours are accurate so customers know when they can order.
          </p>
          <p style={{ fontWeight: "bold", marginBottom: "8px", fontSize: isMobile ? "19px" : "19px" }}>
            To set hours:
          </p>
          <ul style={{ listStyleType: "disc", paddingLeft: "20px", marginBottom: "16px", fontSize: isMobile ? "15px" : "15px" }}>
            <li>Go to the Operating Hours section.</li>
            <li>Update your daily hours of operation.</li>
            <li>Save changes to make them live.</li>
          </ul>
        </>
      ),
    },
    "Safety & hygiene": {
      heading: "Safety and Hygiene Standards",
      content: (
        <>
          <p style={{ marginBottom: "16px", fontSize: isMobile ? "16px" : "17px" }}>
            Berymo requires restaurants to maintain high safety and hygiene standards.
          </p>
          <p style={{ fontWeight: "bold", marginBottom: "8px", fontSize: isMobile ? "19px" : "19px" }}>
            Guidelines:
          </p>
          <ul style={{ listStyleType: "disc", paddingLeft: "20px", marginBottom: "16px", fontSize: isMobile ? "15px" : "15px" }}>
            <li>Ensure all food is prepared in a clean environment.</li>
            <li>Follow local health regulations.</li>
            <li>Use proper packaging to maintain food safety during delivery.</li>
          </ul>
        </>
      ),
    },
    "Analytics & reports": {
      heading: "Analytics and Reports",
      content: (
        <>
          <p style={{ marginBottom: "16px", fontSize: isMobile ? "16px" : "17px" }}>
            Use Berymo’s analytics to track your restaurant’s performance.
          </p>
          <p style={{ fontWeight: "bold", marginBottom: "8px", fontSize: isMobile ? "19px" : "19px" }}>
            To access reports:
          </p>
          <ul style={{ listStyleType: "disc", paddingLeft: "20px", marginBottom: "16px", fontSize: isMobile ? "15px" : "15px" }}>
            <li>Go to the Analytics & Reports section.</li>
            <li>View sales, order trends, and customer insights.</li>
            <li>Use data to make informed decisions.</li>
          </ul>
        </>
      ),
    },
    "Technical issues": {
      heading: "Resolving Technical Issues",
      content: (
        <>
          <p style={{ marginBottom: "16px", fontSize: isMobile ? "16px" : "17px" }}>
            Encountering technical issues? Here’s how to troubleshoot.
          </p>
          <p style={{ fontWeight: "bold", marginBottom: "8px", fontSize: isMobile ? "19px" : "19px" }}>
            Common solutions:
          </p>
          <ul style={{ listStyleType: "disc", paddingLeft: "20px", marginBottom: "16px", fontSize: isMobile ? "15px" : "15px" }}>
            <li>Ensure your app is updated to the latest version.</li>
            <li>Check your internet connection.</li>
            <li>Restart the app or device.</li>
            <li>Contact support if the issue persists.</li>
          </ul>
        </>
      ),
    },
  };

  const handleTopicClick = (topic) => {
    setIsLoading(true);
    setSelectedTopic(topic);
    setIsModalOpen(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 700);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTopic(null);
    setQuery("");
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const filteredTopics = topics.filter((topic) =>
    topic.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div
      style={{
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        color: "#333",
        margin: "0 auto",
      }}
    >
      {/* Header Section - Always visible */}
      <div
        style={{
          backgroundColor: "#FEF4F5",
          padding: isMobile ? "40px 16px" : "60px 0",
          marginTop: isMobile ? "30px" : "0",
        }}
      >
        <div
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            textAlign: "left",
            ...(isMobile ? { paddingLeft: "16px" } : { marginLeft: "360px" }),
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? "24px" : "28px",
              fontWeight: "bold",
              marginBottom: "12px",
              color: "#000",
            }}
          >
            Support Resources for Berymo Places
          </h1>
          <p
            style={{
              fontSize: isMobile ? "14px" : "15px",
              color: "#555",
              lineHeight: "1.5",
              marginBottom: "20px",
            }}
          >
            Looking for help with your restaurant on Berymo? While we don’t offer a customer service number, you can log in to your account to get personalized support or explore the resources below to find solutions to issues related to managing your restaurant on Berymo Places.
          </p>

          {/* Search Bar */}
          <div style={{ position: "relative", width: isMobile ? "100%" : "550px" }}>
            <span
              style={{
                position: "absolute",
                left: "12px",
                top: "40%",
                transform: "translateY(-50%)",
              }}
            >
              <img
                src={searchIcon}
                alt="Search Icon"
                style={{ width: "20px", height: "20px" }}
              />
            </span>
            <input
              type="text"
              placeholder="Search questions, Keywords,Topics"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              style={{
                width: isMobile && isFocused ? "100%" : "100%",
                padding: isMobile ? "12px 14px 12px 40px" : "14px 16px 14px 40px",
                border: `1px solid ${isFocused ? '#fd5c63' : '#fff0f1'}`,
                borderRadius: "5px",
                fontSize: isMobile ? "14px" : "16px",
                color: "#333",
                backgroundColor: isFocused ? "#fff" : "#FFFFFF",
                outline: "none",
                transition: "width 0.3s ease, border-color 0.3s ease, background-color 0.3s ease",
              }}
            />
            {/* Conditional 'X' icon */}
            {query && (
              <span
                onClick={() => setQuery("")}
                style={{
                  position: "absolute",
                  right: window.innerWidth <= 768 ? "12px" : "15px",
                  transform: "translateY(-300%)",
                  cursor: "pointer",
                  color: "#fff",
                  fontSize: "15px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "15px",
                  height: "15px",
                  backgroundColor: "#fd5c63",
                  borderRadius: "50%",
                }}
              >
                <span
                  style={{
                    position: "relative",
                    top: window.innerWidth <= 768 ? "-1px" : "-1px",
                  }}
                >
                  ×
                </span>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div
        style={{
          maxWidth: "800px",
          margin: isMobile ? "0 auto" : "0 0 0 360px",
          padding: isMobile ? "24px 16px 24px 32px" : "40px 0",
        }}
      >
        {isLoading ? (
          <ContentLoader isMobile={isMobile} />
        ) : selectedTopic ? (
          <div
            style={{
              padding: isMobile ? "20px" : "30px",
              borderRadius: isMobile ? "0" : "15px",
              boxShadow: isMobile ? "none" : "0 10px 30px rgba(0, 0, 0, 0.1)",
              backgroundColor: "#fff",
              position: isMobile ? "fixed" : "relative",
              top: isMobile ? "0" : "auto",
              left: isMobile ? "0" : "auto",
              width: isMobile ? "100%" : "auto",
              height: isMobile ? "100%" : "auto",
              overflowY: isMobile ? "auto" : "visible",
              zIndex: isMobile ? "1000" : "auto",
              transform: isMobile ? "translateY(0)" : "none",
              transition: "transform 0.3s ease-in-out",
            }}
          >
            <button
              style={{
                position: "absolute",
                top: isMobile ? "35px" : "15px",
                right: isMobile ? "-150px" : "-355px",
                background: "none",
                border: "none",
                fontSize: isMobile ? "38px" : "38px",
                fontWeight: "300",
                color: "#000",
                cursor: "pointer",
                padding: "5px",
                zIndex: "1001",
                transition: "color 0.3s, transform 0.2s",
              }}
              onMouseOver={(e) => (e.target.style.color = "#fd5c63")}
              onMouseOut={(e) => (e.target.style.color = "#000")}
              onClick={closeModal}
            >
              ×
            </button>
            <h2
              style={{
                fontSize: isMobile ? "24px" : "28px",
                fontWeight: "bold",
                marginBottom: "20px",
                color: "#222",
                paddingTop: isMobile ? "30px" : "0",
              }}
            >
              {topicContent[selectedTopic]?.heading}
            </h2>
            <div
              style={{
                fontSize: isMobile ? "14px" : "16px",
                color: "#555",
                lineHeight: "1.6",
              }}
            >
              {topicContent[selectedTopic]?.content}
            </div>
            <button
              style={{
                marginTop: "30px",
                padding: isMobile ? "12px 24px" : "14px 28px",
                backgroundColor: "#fd5c63",
                color: "#fff",
                border: "none",
                borderRadius: "25px",
                fontSize: isMobile ? "14px" : "16px",
                fontWeight: "bold",
                cursor: "pointer",
                display: "block",
                margin: "30px auto 0",
                transition: "background-color 0.3s, transform 0.2s",
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = "#474747";
                e.target.style.transform = "scale(1.05)";
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = "#fd5c63";
                e.target.style.transform = "scale(1)";
              }}
              onClick={() => (window.location.href = "https://place-sit.berymo.com")}
            >
              Log in to Get Help
            </button>
          </div>
        ) : (
          <div>
            <h2
              style={{
                fontSize: isMobile ? "20px" : "24px",
                fontWeight: "bold",
                marginBottom: "20px",
                color: "#000",
                textAlign: "left",
              }}
            >
              All Topics
            </h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {filteredTopics.map((topic, index) => (
                <li
                  key={index}
                  onClick={() => handleTopicClick(topic)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: isMobile ? "16px 0" : "20px 0",
                    borderBottom: "1px solid #eee",
                    fontSize: isMobile ? "14px" : "16px",
                    color: "#000",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f9f9f9")}
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ marginRight: "10px", fontSize: isMobile ? "18px" : "20px" }}>
                      ≡
                    </span>
                    {topic}
                  </div>
                  <span style={{ fontSize: isMobile ? "18px" : "20px", color: "#999" }}>›</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpBerymo;